import {useContext, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useKeycloak} from '@react-keycloak/web';

import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Select,
  Spin,
  Upload,
} from 'antd';
import {UploadOutlined} from '@ant-design/icons';
import {MediaContext} from './MediaContext';
import {
  createMedia,
  findMediaById,
  updateMedia,
  uploadMedia,
} from 'services/MediaService';
import {handleError} from 'utils/MessageUtils';
import {humanFileSize, isValidUrl} from 'utils/Utils';
import {MediaType, UPLOAD_MAX_FILE_SIZE} from 'shared/constants/AppConst';

const MediaEditor = () => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const [form] = Form.useForm();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const mediaContext = useContext(MediaContext);
  const {selectedMediaId, selectedRef, setReloading, editing, setEditing} =
    mediaContext;
  const [media, setMedia] = useState();
  const [file, setFile] = useState();
  const [actionType, setActionType] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editing && selectedMediaId) {
      setLoading(true);
      findMediaById(headers, selectedMediaId)
        .then((res) => {
          setMedia(res.data);
          form.resetFields();
        })
        .catch((error) => handleError(message, error))
        .finally(() => setLoading(false));
    } else {
      setMedia(null);
      form.resetFields();
    }
  }, [editing]);

  useEffect(() => {
    if (media?.type === MediaType.DICOM) {
      setActionType('URL');
    } else if (isValidUrl(media?.contentId)) {
      setActionType('URL');
    } else {
      setActionType('UPLOAD');
    }
  }, [media]);

  const onCancel = () => {
    setEditing(false);
  };

  const onSelectFile = (info) => {
    setFile(info.fileList[0]);
  };

  const onRemoveFile = () => {
    setFile(null);
  };

  const onTypeChange = (value) => {
    if (value === MediaType.DICOM) {
      setActionType('URL');
    } else {
      setActionType('UPLOAD');
    }
  };

  const onActionChange = (e) => {
    setActionType(e.target.value);
  };

  const onSave = () => {
    form.validateFields().then((values) => {
      const data = {
        id: media?.id,
        title: values.title,
        contentId: values.url,
        type: values.type,
        note: values.note,
        reference: selectedRef,
      };

      if (file) {
        const formData = new FormData();
        formData.append('file', file.originFileObj);
        formData.append(
          'media',
          new Blob([JSON.stringify(data)], {type: 'application/json'}),
        );

        uploadMedia(headers, formData)
          .then(() => {
            setEditing(false);
            setReloading(true);
            message.success(intl.messages['common.created']);
          })
          .catch((error) => handleError(message, error));
      } else if (media) {
        console.log(JSON.stringify(values));
        updateMedia(headers, media.id, {
          title: values.title,
          contentId: values.url || media.contentId,
          type: values.type,
          note: values.note ? values.note : media.note,
        })
          .then(() => {
            setEditing(false);
            setReloading(true);
            message.success(intl.messages['common.updated']);
          })
          .catch((error) => handleError(message, error));
      } else {
        createMedia(headers, data)
          .then(() => {
            setEditing(false);
            setReloading(true);
            message.success(intl.messages['common.created']);
          })
          .catch((error) => handleError(message, error));
      }
    });
  };

  return (
    <Modal
      title={intl.messages['common.detail']}
      centered
      visible={editing}
      okText={intl.messages['common.save']}
      onCancel={() => onCancel()}
      onOk={() => onSave()}
      destroyOnClose={true}
      width={500}>
      <Spin spinning={loading}>
        <Form
          form={form}
          layout='vertical'
          initialValues={{
            ...media,
            url: isValidUrl(media?.contentId) ? media.contentId : null,
          }}>
          <Form.Item
            name='title'
            label={intl.messages['media.title']}
            rules={[
              {
                required: !media,
                message: `${intl.messages['media.title']} ${intl.messages['validation.required']}`,
              },
            ]}>
            <Input />
          </Form.Item>
          <Form.Item
            name='type'
            label={intl.messages['media.type']}
            rules={[
              {
                required: !media,
                message: `${intl.messages['media.type']} ${intl.messages['validation.required']}`,
              },
            ]}>
            <Select onChange={onTypeChange}>
              <Select.Option value={MediaType.IMAGE}>
                {intl.messages['media.type.image']}
              </Select.Option>
              <Select.Option value={MediaType.AUDIO}>
                {intl.messages['media.type.audio']}
              </Select.Option>
              <Select.Option value={MediaType.VIDEO}>
                {intl.messages['media.type.video']}
              </Select.Option>
              <Select.Option value={MediaType.DOCUMENT}>
                {intl.messages['media.type.document']}
              </Select.Option>
              <Select.Option value={MediaType.DICOM}>
                {intl.messages['media.type.dicom']}
              </Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Radio.Group onChange={onActionChange} value={actionType}>
              <Radio value='URL'>URL</Radio>
              <Radio
                value='UPLOAD'
                disabled={form.getFieldValue('type') === MediaType.DICOM}>
                Upload File
              </Radio>
            </Radio.Group>
          </Form.Item>
          {actionType === 'URL' && (
            <Form.Item
              name='url'
              label='URL'
              rules={[
                {
                  type: 'url',
                  message: intl.messages['validation.url'],
                },
              ]}>
              <Input />
            </Form.Item>
          )}
          {actionType === 'UPLOAD' && (
            <Form.Item name='file' label='File'>
              <Upload
                maxCount={1}
                onChange={onSelectFile}
                onRemove={onRemoveFile}
                beforeUpload={(file) => {
                  if (file.size > UPLOAD_MAX_FILE_SIZE) {
                    message.error(
                      `${
                        intl.messages['error.maximumUploadFileSize']
                      }: ${humanFileSize(UPLOAD_MAX_FILE_SIZE)}`,
                    );
                    return true;
                  } else {
                    return false;
                  }
                }}>
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>
            </Form.Item>
          )}
          <Form.Item name='note' label={intl.messages['media.note']}>
            <Input.TextArea rows={4} showCount maxLength={500} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default MediaEditor;
